import { useState, useCallback, useMemo } from 'react';

interface UseSelectionReturn {
  selectedIds: ReadonlySet<string>;
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  selectAll: (ids: string[]) => void;
  clearAll: () => void;
  isAllSelected: (ids: string[]) => boolean;
  isAnySelected: boolean;
  selectedCount: number;
}

interface SelectionState {
  key: string;
  ids: ReadonlySet<string>;
}

/**
 * Hook for managing multi-select state with automatic reset.
 *
 * Selection clears automatically when resetKey changes (e.g., page number,
 * collection filter, sort order). This prevents stale selections from
 * persisting across context changes.
 *
 * Uses derived state pattern (storing key alongside state) to avoid
 * useEffect + setState and ref-during-render issues with React 19.
 */
export function useSelection(resetKey: string): UseSelectionReturn {
  const [state, setState] = useState<SelectionState>({ key: resetKey, ids: new Set() });

  // Derive current selection: reset if key changed since last render
  const selectedIds = state.key === resetKey ? state.ids : new Set<string>();

  // Sync key into state when it diverges (schedules re-render with clean state)
  if (state.key !== resetKey) {
    setState({ key: resetKey, ids: new Set() });
  }

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds]
  );

  const toggle = useCallback((id: string) => {
    setState(prev => {
      const next = new Set(prev.ids);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return { ...prev, ids: next };
    });
  }, []);

  const selectAll = useCallback((ids: string[]) => {
    setState(prev => ({ ...prev, ids: new Set(ids) }));
  }, []);

  const clearAll = useCallback(() => {
    setState(prev => ({ ...prev, ids: new Set() }));
  }, []);

  const isAllSelected = useCallback(
    (ids: string[]) => ids.length > 0 && ids.every(id => selectedIds.has(id)),
    [selectedIds]
  );

  const isAnySelected = selectedIds.size > 0;
  const selectedCount = selectedIds.size;

  return useMemo(() => ({
    selectedIds,
    isSelected,
    toggle,
    selectAll,
    clearAll,
    isAllSelected,
    isAnySelected,
    selectedCount,
  }), [selectedIds, isSelected, toggle, selectAll, clearAll, isAllSelected, isAnySelected, selectedCount]);
}
